import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Supported UI languages. Keep in sync with `supportedLngs` below and with
 * the `public/locales/<lng>/` directory set.
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'ru', 'ja'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABEL_KEYS: Record<SupportedLanguage, string> = {
  en: 'language.english',
  es: 'language.spanish',
  ru: 'language.russian',
  ja: 'language.japanese',
};

/**
 * localStorage key used to persist the user's language choice across sessions.
 * Read by the LanguageDetector below and written by the language switcher.
 */
export const LANGUAGE_STORAGE_KEY = 'app_language';

/**
 * Type guard narrowing an arbitrary string to a SupportedLanguage.
 *
 * @param value - candidate language code (may come from URL, backend, or storage)
 * @returns true if `value` is one of the supported languages
 */
export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

const isDev = process.env.NODE_ENV === 'development';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    load: 'languageOnly',
    ns: ['common', 'screens', 'errors', 'settings', 'auth'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    // Treat empty-string translations as missing so defaultValue (English source)
    // is used instead. Without this, the es/screens.json skeleton's "" values
    // shadow the English defaultValue and all text disappears when switching to ES.
    returnEmptyString: false,
    react: {
      useSuspense: true,
    },
    saveMissing: isDev,
    debug: isDev,
    missingKeyHandler: isDev
      ? (lngs, ns, key) => {
          // eslint-disable-next-line no-console
          console.warn('[i18n] missing key', { lngs, ns, key });
        }
      : undefined,
  });

export default i18n;
